// @flow
import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { FormControl, FormGroup, Checkbox, Radio, Button, Row, Col } from 'react-bootstrap';

import { connect } from 'react-redux';
import { setAuthStatus, setUserName } from '../../redux/actionCreators';

import type { UserRegistrationType } from '../../utils/types';
import type { ValidationError } from '../Validation/Validator';

import ValidatedField from '../Validation/ValidatedField';
import Validator from '../Validation/Validator';

import Api from '../../api/api';

export type PersonRegistrationData = {
  ...UserRegistrationType,
  password_confirmation: string,
  agreedToTermsOfService: boolean,
};

type Props = {
  history: Object,
  data: PersonRegistrationData,
  showGiftForm: boolean,
  handleChangeAuthStatus: (authenticated: boolean) => void,
  setUserName: (userName: string) => void,
  handleNavButtonClick: (data: PersonRegistrationData) => void,
  toggleSpinner: () => void,
};

type State = {
  data: PersonRegistrationData,
  errors: Array<ValidationError>,
};

class PersonRegistrationForm extends Component<Props, State> {
  state = {
    data: this.props.data,
    errors: [],
  };

  componentWillReceiveProps = nextProps => {
    if (nextProps.data.is_gift !== this.state.data.is_gift) {
      this.setState({ data: nextProps.data });
    }
  };

  getValidator = (): Object => {
    if (this.state.data.is_gift) {
      return new Validator({
        password: [Validator.min(8)],
        password_confirmation: [Validator.same('password')],
        agreedToTermsOfService: [Validator.required],
      });
    }

    if (this.props.showGiftForm) {
      return new Validator({
        sender_first_name: [Validator.required, Validator.noForbiddenSigns],
        sender_last_name: [Validator.required, Validator.noForbiddenSigns],
        sender_email: [Validator.required, Validator.email],
        first_name: [Validator.required, Validator.noForbiddenSigns],
        last_name: [Validator.required, Validator.noForbiddenSigns],
        email: [Validator.required, Validator.email],
        agreedToTermsOfService: [Validator.required],
      });
    }

    return new Validator({
      first_name: [Validator.required, Validator.noForbiddenSigns],
      last_name: [Validator.required, Validator.noForbiddenSigns],
      email: [Validator.required, Validator.email],
      password: [Validator.min(8)],
      password_confirmation: [Validator.same('password')],
      agreedToTermsOfService: [Validator.required],
    });
  };

  handlePersonalDataInput = (e: { target: HTMLInputElement }): void => {
    this.setState({
      data: {
        ...this.state.data,
        [e.target.id]: e.target.value,
      },
    });
  };

  checkEmail = (): void => Api.users.checkEmail(this.state.data.email);

  handleTermsOfServiceClick = (e: { target: HTMLInputElement }): void =>
    this.setState({
      data: { ...this.state.data, agreedToTermsOfService: e.target.checked },
    });

  handleNavButtonClick = (): void => {
    this.setState({ errors: [] }, async (): Promise<void> => {
      const { history, handleChangeAuthStatus, setUserName, toggleSpinner } = this.props;
      const { data } = this.state;
      const validator = this.getValidator();

      const errors = validator.validate(this.state.data);

      if (!data.is_gift && (await this.checkEmail())) {
        errors.push({ field: 'email', message: 'Email in use' });
      }

      if (errors.length) {
        return this.setState({ errors });
      }

      if (data.is_gift) {
        toggleSpinner();

        return Api.users
          .updateUserData({
            user: {
              password: data.password,
              password_confirmation: data.password_confirmation,
              subscription_method: data.subscription_method,
            },
          })
          .then(({ first_name, last_name }) => {
            handleChangeAuthStatus(true);
            setUserName(`${first_name} ${last_name}`);
            toggleSpinner();

            return history.push('/profile');
          });
      }

      this.props.handleNavButtonClick(data);
    });
  };

  render() {
    const { history, showGiftForm } = this.props;
    const { subscription_method, is_gift } = this.state.data;

    return (
      <div>
        <h3>Account Information</h3>

        {showGiftForm && (
          <Fragment>
            <FormGroup>
              <ValidatedField field="sender_first_name" errors={this.state.errors}>
                <FormControl
                  id="sender_first_name"
                  onChange={this.handlePersonalDataInput}
                  value={this.state.data.sender_first_name}
                  type="text"
                  placeholder="Sender's First Name"
                  autoComplete="given-name"
                />
              </ValidatedField>
            </FormGroup>
            <FormGroup>
              <ValidatedField field="sender_last_name" errors={this.state.errors}>
                <FormControl
                  id="sender_last_name"
                  onChange={this.handlePersonalDataInput}
                  value={this.state.data.sender_last_name}
                  type="text"
                  placeholder="Sender's Last Name"
                  autoComplete="family-name"
                />
              </ValidatedField>
            </FormGroup>
            <FormGroup>
              <ValidatedField field="sender_email" errors={this.state.errors}>
                <FormControl
                  readOnly={is_gift}
                  id="sender_email"
                  onChange={this.handlePersonalDataInput}
                  value={this.state.data.sender_email}
                  type="text"
                  placeholder="Sender's Email Address"
                  autoComplete="email"
                />
              </ValidatedField>
            </FormGroup>
          </Fragment>
        )}

        <FormGroup>
          <ValidatedField field="first_name" errors={this.state.errors}>
            <FormControl
              readOnly={is_gift}
              id="first_name"
              onChange={this.handlePersonalDataInput}
              value={this.state.data.first_name}
              type="text"
              placeholder={showGiftForm ? "Recipient's First Name" : 'First Name'}
              autoComplete="given-name"
            />
          </ValidatedField>
        </FormGroup>
        <FormGroup>
          <ValidatedField field="last_name" errors={this.state.errors}>
            <FormControl
              readOnly={is_gift}
              id="last_name"
              onChange={this.handlePersonalDataInput}
              value={this.state.data.last_name}
              type="text"
              placeholder={showGiftForm ? "Recipient's Last Name" : 'Last Name'}
              autoComplete="family-name"
            />
          </ValidatedField>
        </FormGroup>
        <FormGroup>
          <ValidatedField field="email" errors={this.state.errors}>
            <FormControl
              readOnly={is_gift}
              id="email"
              onChange={this.handlePersonalDataInput}
              value={this.state.data.email}
              type="email"
              placeholder={showGiftForm ? "Recipient's Email Address" : 'Email Address'}
              autoComplete="email"
            />
          </ValidatedField>
        </FormGroup>
        {showGiftForm ? (
          <FormGroup>
            <ValidatedField field="message" errors={this.state.errors}>
              <FormControl
                id="message"
                onChange={this.handlePersonalDataInput}
                value={this.state.data.message}
                componentClass="textarea"
                placeholder="Add a gift message (optional)"
              />
            </ValidatedField>
          </FormGroup>
        ) : (
          <Fragment>
            <FormGroup>
              <ValidatedField field="password" errors={this.state.errors}>
                <FormControl
                  id="password"
                  onChange={this.handlePersonalDataInput}
                  value={this.state.data.password}
                  type="password"
                  placeholder="Password"
                  autoComplete="new-password"
                />
              </ValidatedField>
            </FormGroup>
            <FormGroup>
              <ValidatedField field="password_confirmation" errors={this.state.errors}>
                <FormControl
                  id="password_confirmation"
                  onChange={this.handlePersonalDataInput}
                  value={this.state.data.password_confirmation}
                  type="password"
                  placeholder="Repeat Password"
                  autoComplete="new-password"
                />
              </ValidatedField>
            </FormGroup>
          </Fragment>
        )}

        {is_gift && (
          <Fragment>
            <FormGroup className="person-registr">
              <div className="web-btn">
                <Radio
                  className="radio"
                  name="radioGroup"
                  inline
                  checked={subscription_method === 'web'}
                  onChange={() => this.setState({ data: { ...this.state.data, subscription_method: 'web' } })}
                  // style={{ color: 'black' }}
                >
                  <span className="radio-text" />
                  <span className="choice-radio">Web Subscription</span>
                </Radio>
              </div>
              <div className="mobile-btn">
                <Radio
                  className="radio"
                  name="radioGroup"
                  inline
                  checked={subscription_method === 'mobile'}
                  onChange={() => this.setState({ data: { ...this.state.data, subscription_method: 'mobile' } })}
                  // style={{ color: 'black' }}
                >
                  <span className="radio-text" />
                  <span className="choice-radio">Mobile Subscription</span>
                </Radio>
                {subscription_method === 'mobile' && (
                  <div className="text-please-download">
                    <span>
                      <a
                        href="https://itunes.apple.com/us/app/presence-walk-with-god/id1400174335?l=ru&ls=1&mt=8"
                        target="_blank"
                        style={{ color: '#202020' }}
                      >
                        Please download the Presence app from the Apple App Store.
                      </a>
                    </span>
                  </div>
                )}
              </div>
            </FormGroup>
          </Fragment>
        )}

        <Row>
          <div className="form-footer for-error">
            <Col md={6} className="form-agree">
              <FormGroup>
                <ValidatedField field="agreedToTermsOfService" errors={this.state.errors}>
                  <Checkbox
                    inline
                    checked={this.state.data.agreedToTermsOfService}
                    onChange={this.handleTermsOfServiceClick}
                  >
                    <span className="checkbox-custom" />
                    <span className="first-terms">
                      I agree{' '}
                      <a className="label" onClick={() => history.push('/terms-of-service')}>
                        <span className="agree-terms">to the terms of service</span>
                      </a>
                    </span>
                  </Checkbox>
                </ValidatedField>
              </FormGroup>
            </Col>
            <Col md={6} className="form-send-button next-step">
              <Button bsStyle="info" onClick={this.handleNavButtonClick}>
                Next Step
              </Button>
            </Col>
          </div>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = state => ({ showGiftForm: state.registration.showGiftForm });

const mapDispatchToProps = (dispatch: Function) => ({
  handleChangeAuthStatus(authenticated) {
    dispatch(setAuthStatus(authenticated));
  },

  setUserName(userName) {
    dispatch(setUserName(userName));
  },
});

PersonRegistrationForm = connect(mapStateToProps, mapDispatchToProps)(PersonRegistrationForm);

PersonRegistrationForm = withRouter(PersonRegistrationForm);

export default PersonRegistrationForm;



// WEBPACK FOOTER //
// ./src/components/Register/PersonRegistrationForm.js